import { useEffect, useState } from 'react';
import { PrivateRoutes, AuthRoutes } from 'src/router';
import { CurrentUserSessionHoc, AppProvider, PaymentProvider } from 'src/hoc';
import { SiteLoader } from '@common/components';
import { Auth } from 'aws-amplify';
import {
  setUserIdAction,
  useAppDispatch,
  useAppSelector,
  setIsAuthorizeAction,
  setIsSuperAdminAction,
} from 'src/store';
import { useAlert } from '@common/hooks';
import { useTranslation } from 'react-i18next';
import { apiInstance } from './services';


const fetchKeyConfig = async () => {
  const url = '/console-config/env.json';
  const response = await fetch(url);
  const config = await response.json();
  const apiUrl = config.apiUrl;

  const keys = {
    dev: "G-56Z7T2DJH6",
    test: "G-B0S7WEJQK5",
    staging: "G-8PZF3FH91X",
    prod: "G-XLYQFLGV5G"
  };
  const match = apiUrl.match(/https:\/\/(\w+)\.vially\.io/);
  const env = match ? match[1] as keyof typeof keys : null;

  if (env && ['dev', 'test', 'staging', 'prod'].includes(env)) {
    return keys[env];
  }
  return keys.dev;

};
const App = (): JSX.Element => {
  const [c] = useTranslation('common');
  const { showAlert } = useAlert();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { isAuthorize } = useAppSelector((state) => state.user.data);
  const { isSuperAdmin } = useAppSelector((state) => state.user.data);
  useEffect(() => {
    const getTokenResponse = async (): Promise<void> => {
      try {
        const data = await Auth.currentSession();
        const idToken: string = data.getIdToken().getJwtToken();
        dispatch(setUserIdAction(idToken));
        dispatch(setIsAuthorizeAction(true));
      } catch (error) {
        if (error instanceof Error) {
          dispatch(setIsAuthorizeAction(false));
          showAlert({
            id: error.message,
            title: c('Error'),
            message: error.message,
            variant: 'error',
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    getTokenResponse();
  }, [isAuthorize]);

  useEffect(() => {
    checkAuthState();
    getVersion()
  }, [isAuthorize]);

  const checkAuthState = async (): Promise<void> => {
    try {
      const session = await Auth.currentSession();
      const userRole = session.getAccessToken().payload['cognito:groups'];
      dispatch(setIsSuperAdminAction(userRole?.includes('ADMINISTRATOR') || false));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setIsSuperAdminAction(false));
        showAlert({
          id: error.message,
          title: c('Error'),
          message: error.message,
          variant: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  async function getVersion() {
    const cacheKey = 'versionInfo';
    const cacheTTL = 1000 * 60 * 60 * 6; // 6 hours
  
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { timestamp, data } = JSON.parse(cached);
      if (Date.now() - timestamp < cacheTTL) {
        console.log(`%c Engine Image Id: ${data.amiName}`, 'color: #50C878');
        console.log(`%c Layer version: ${data.version}`, 'color: #50C878');
        return;
      }
    }
  
    try {
      const [amNameRes, versionRes] = await Promise.all([
        apiInstance.get('/reporttool/ami-name'),
        apiInstance.get('/reporttool/version'),
      ]);
  
      const data = {
        amiName: amNameRes?.data?.amiName,
        version: versionRes?.data?.version,
      };
  
      localStorage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), data }));
  
      console.log(`%c Engine Image Id: ${data.amiName}`, 'color: #50C878');
      console.log(`%c Layer version: ${data.version}`, 'color: #50C878');
    } catch (err) {
      console.error(err);
    }
  }
  
  // useEffect(() => {
  //   // Define the script content
  //   const scriptContent = `
  //           window.intercomSettings = {
  //               app_id: "ifal4oli"
  //           };
  //           (function() {
  //           var w = window;
  //           var ic = w.Intercom;
  //           if (typeof ic === "function") {
  //               ic('reattach_activator');
  //               ic('update', w.intercomSettings);
  //           } else {
  //               var d = document;
  //               var i = function() { i.c(arguments); };
  //               i.q = [];
  //               i.c = function(args) { i.q.push(args); };
  //               w.Intercom = i;
  //               var l = function() {
  //               var s = d.createElement('script');
  //               s.type = 'text/javascript';
  //               s.async = true;
  //               s.src = 'https://widget.intercom.io/widget/ifal4oli';
  //               var x = d.getElementsByTagName('script')[0];
  //               x.parentNode.insertBefore(s, x);
  //               };
  //               if (d.readyState === 'complete') {
  //               l();
  //               } else if (w.attachEvent) {
  //               w.attachEvent('onload', l);
  //               } else {
  //               w.addEventListener('load', l, false);
  //               }
  //           }

  //           window.dataLayer = window.dataLayer || [];
  //           function gtag(){dataLayer.push(arguments);}
  //           gtag('js', new Date());
  //           gtag('config', 'G-XLYQFLGV5G');
  //           })();
  //   `;

  //   // Create a script element
  //   const script = document.createElement('script');
  //   script.type = 'text/javascript';
  //   script.innerHTML = scriptContent;

  //   // Append the script to the document body
  //   document.body.appendChild(script);

  //   // Cleanup function to remove the script when the component unmounts
  //   return () => {
  //       document.body.removeChild(script);
  //   };
  // }, []);

  const setUpGoogleAnalytics = async () => {
    const key = await fetchKeyConfig()

    const script1 = document.createElement('script');
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${key}`;
    script1.type = 'text/javascript';
    script1.async = true;
    document.head.appendChild(script1);
    const scriptContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
  
    gtag('config', '${key}');
    `;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.innerHTML = scriptContent;
    document.head.appendChild(script);
    return () => {
      document.body.removeChild(script);
      document.body.removeChild(script1);
    };
  }



  useEffect(() => {
    setUpGoogleAnalytics()
    return () => {

    };
  }, []);
  if (isLoading) {
    return <SiteLoader />;
  }
  const urlParams = new URLSearchParams(location.search);
  const code: string = urlParams.get('code') as string;
  
  if (isAuthorize && !code) {
    return (
      <AppProvider>
        <div>
          <span tabIndex={-1} style={{ position: 'absolute', left: '-200px' }}>
            {'\u00A0'}
          </span>
          <a id="skipLink" href="#mainContent">
            Skip to content
          </a>
        </div>
        {(isSuperAdmin && <>
          {/* <RedirectRoutes/> */}
          <PrivateRoutes /></>) || (
            <CurrentUserSessionHoc>
              <PaymentProvider/>   
            </CurrentUserSessionHoc>
          )}
      </AppProvider>
    );
  }

  return (
    <AppProvider>
      <div>
        <span tabIndex={-1} style={{ position: 'absolute', left: '-200px' }}>
          {'\u00A0'}
        </span>
        <a id="skipLink" href="#mainContent">
          Skip to content
        </a>
      </div>
      <AuthRoutes />
    </AppProvider>
  );
};

export default App;
